import 'reflect-metadata';
import {
  X509Certificate,
  TextConverter,
  SubjectAlternativeNameExtension,
  BasicConstraintsExtension,
  ExtendedKeyUsageExtension,
  Extension,
} from '@peculiar/x509';
import { readTlv, TAG } from '../bytes/der';
import { sha256 } from '../crypto/sha256';
import { parseSctList } from '../rfc6962/sct';
import type { Sct } from '../rfc6962/types';
import { wrapTbsAsCertificate } from './tbs';

export const OID = {
  ctPoison: '1.3.6.1.4.1.11129.2.4.3',
  sctList: '1.3.6.1.4.1.11129.2.4.2',
  ctPrecertSigning: '1.3.6.1.4.1.11129.2.4.4',
  subjectAltName: '2.5.29.17',
  basicConstraints: '2.5.29.19',
  keyUsage: '2.5.29.15',
  extKeyUsage: '2.5.29.37',
  authorityKeyId: '2.5.29.35',
  subjectKeyId: '2.5.29.14',
  crlDistributionPoints: '2.5.29.31',
  authorityInfoAccess: '1.3.6.1.5.5.7.1.1',
  certificatePolicies: '2.5.29.32',
} as const;

const EXTRA_NAMES: Record<string, string> = {
  [OID.ctPoison]: 'CT Precertificate Poison',
  [OID.sctList]: 'CT Precertificate SCTs',
  [OID.ctPrecertSigning]: 'CT Precertificate Signing',
  '1.3.6.1.5.5.7.1.24': 'TLS Feature',
  '2.5.29.18': 'Issuer Alternative Name',
  '2.5.29.30': 'Name Constraints',
  '2.5.29.36': 'Policy Constraints',
  '2.5.29.54': 'Inhibit Any Policy',
  '1.3.6.1.5.5.7.1.11': 'Subject Info Access',
  '2.5.29.9': 'Subject Directory Attributes',
  '1.2.840.113549.1.9.15': 'S/MIME Capabilities',
  '2.16.840.1.113730.1.1': 'Netscape Cert Type',
  '2.16.840.1.113730.1.13': 'Netscape Comment',
};

export interface ExtensionView {
  oid: string;
  name: string;
  critical: boolean;
  /** extnValue content (DER of the extension's own value) */
  value: Uint8Array;
  /** Whole Extension SEQUENCE DER */
  raw: Uint8Array;
  /** Human-readable rendering, when the library knows the extension */
  text?: string;
}

export interface ParsedCert {
  der: Uint8Array;
  /** True when built from a bare TBSCertificate (precert entry) — the signature is fake. */
  isTbsOnly: boolean;
  version: number;
  serialHex: string;
  subject: string;
  issuer: string;
  /** Best short label: CN, else first SAN, else full subject. */
  commonName: string;
  notBefore: Date;
  notAfter: Date;
  sans: string[];
  publicKeyAlgorithm: string;
  signatureAlgorithm: string;
  spki: Uint8Array;
  isCA: boolean;
  extendedKeyUsages: string[];
  /** RFC 6962 §3.1: a CA-issued cert with the CT Precertificate Signing EKU; precerts it signs use the CA's key hash. */
  isPrecertSigningCert: boolean;
  hasPoison: boolean;
  /** Embedded SCTs (from the 1.3.6.1.4.1.11129.2.4.2 extension), when present */
  scts: Sct[];
  sctParseError?: string;
  extensions: ExtensionView[];
  /** underlying object for callers that want more */
  cert: X509Certificate;
}

function algoName(a: Algorithm & { namedCurve?: string; modulusLength?: number; hash?: { name: string } | string }): string {
  const hash = typeof a.hash === 'string' ? a.hash : a.hash?.name;
  const name = a.name === 'RSASSA-PKCS1-v1_5' ? 'RSA' : a.name;
  const parts = [name];
  if (a.namedCurve) parts.push(a.namedCurve);
  if (a.modulusLength) parts.push(`${a.modulusLength}-bit`);
  if (hash && hash !== name) parts.push(`with ${hash}`);
  return parts.join(' ');
}

function extensionName(ext: Extension): string {
  const ctor = ext.constructor as { NAME?: string };
  // Known extensions are subclasses with their own NAME; the base class means "unknown".
  return EXTRA_NAMES[ext.type] ?? (ctor !== Extension && ctor.NAME ? ctor.NAME : ext.type);
}

function extensionText(ext: Extension): string | undefined {
  if (ext.constructor === Extension) return undefined; // unknown extension: only raw bytes
  try {
    return TextConverter.serialize(ext.toTextObject()).trim();
  } catch {
    return undefined;
  }
}

/** Reads the TLS SignedCertificateTimestampList out of the SCT list extension value. */
export function sctListFromExtensionValue(value: Uint8Array): Sct[] {
  const t = readTlv(value, 0);
  const inner = t.tag === TAG.OCTET_STRING ? t.content : value;
  return parseSctList(inner);
}

export function parseCertificate(der: Uint8Array, isTbsOnly = false): ParsedCert {
  const cert = new X509Certificate(der as BufferSource);
  const extensions: ExtensionView[] = cert.extensions.map((e) => {
    const view: ExtensionView = {
      oid: e.type,
      name: extensionName(e),
      critical: e.critical,
      value: new Uint8Array(e.value),
      raw: new Uint8Array(e.rawData),
    };
    const text = extensionText(e);
    if (text) view.text = text;
    return view;
  });
  const san = cert.getExtension(SubjectAlternativeNameExtension);
  const sans = san ? san.names.items.map((n) => (n.type === 'dns' || n.type === 'email' || n.type === 'url' || n.type === 'ip' ? n.value : `${n.type}:${n.value}`)) : [];
  const bc = cert.getExtension(BasicConstraintsExtension);
  const eku = cert.getExtension(ExtendedKeyUsageExtension);
  const extendedKeyUsages = eku ? eku.usages.map(String) : [];
  const cnMatch = /(?:^|, )CN=((?:"[^"]*"|[^,])+)/.exec(cert.subject);
  const commonName = cnMatch?.[1]?.replace(/^"|"$/g, '') || sans[0] || cert.subject || '(empty subject)';

  let scts: Sct[] = [];
  let sctParseError: string | undefined;
  const sctExt = extensions.find((e) => e.oid === OID.sctList);
  if (sctExt) {
    try {
      scts = sctListFromExtensionValue(sctExt.value);
    } catch (e) {
      sctParseError = e instanceof Error ? e.message : String(e);
    }
  }
  const out: ParsedCert = {
    der,
    isTbsOnly,
    version: (cert as unknown as { version?: number }).version ?? 3,
    serialHex: cert.serialNumber.toLowerCase(),
    subject: cert.subject,
    issuer: cert.issuer,
    commonName,
    notBefore: cert.notBefore,
    notAfter: cert.notAfter,
    sans,
    publicKeyAlgorithm: algoName(cert.publicKey.algorithm as Algorithm),
    signatureAlgorithm: algoName(cert.signatureAlgorithm as Algorithm),
    spki: new Uint8Array(cert.publicKey.rawData),
    isCA: bc?.ca ?? false,
    extendedKeyUsages,
    isPrecertSigningCert: extendedKeyUsages.includes(OID.ctPrecertSigning),
    hasPoison: extensions.some((e) => e.oid === OID.ctPoison),
    scts,
    extensions,
    cert,
  };
  if (sctParseError) out.sctParseError = sctParseError;
  return out;
}

/** Parses the poison-free TBSCertificate carried in precert log entries. */
export function parseTbs(tbs: Uint8Array): ParsedCert {
  return parseCertificate(wrapTbsAsCertificate(tbs), true);
}

export const certFingerprint = (der: Uint8Array): Promise<Uint8Array> => sha256(der);
